// The answer to a COUNTERPARTY_UNCLASSIFIED blocker.
//
// Extracted from the Documents workbench when the Working rules screen grew a
// list of the same blockers, and extracted rather than copied for a reason the
// tests state: "pre-selecting the UNSAFE option in the UI turns the safe-default
// test red." Two copies of this control is two places for that to stop being
// true, and the second one would be the copy nobody re-read.

import { useState, type ReactNode } from 'react';
import { useT } from '../i18n/index.tsx';

export type CounterpartyClassification = 'one_off' | 'recurring_business';

/**
 * The answer to a COUNTERPARTY_UNCLASSIFIED blocker, where the reviewer is.
 *
 * ch.06's rule for this screen is that a control must not make a decision look
 * smaller than it is, and this is a fiscal decision: it is what says whether the
 * office's relations list grows. So:
 *
 *   * The conservative answer is PRE-SELECTED and says why. The server has no
 *     default — a default there would be the API deciding and then recording a
 *     person's name against it — but a reviewer who is unsure should be able to
 *     see what the safe answer is rather than having to know.
 *   * Both options say what actually happens, including the part people get
 *     wrong: "recurring" does not create a relation either. It says this party
 *     deserves one, and which relation that is stays a mapping somebody else
 *     approves. A control that implied otherwise would be offering a reviewer a
 *     power they do not have.
 *   * Nothing is pre-submitted. The reviewer picks and presses.
 */
export function ClassifyCounterparty({
  working,
  onClassify,
}: {
  readonly working: boolean;
  readonly onClassify: (classification: CounterpartyClassification) => void;
}): ReactNode {
  const t = useT();
  // The conservative answer, pre-selected. See the note above.
  const [choice, setChoice] = useState<CounterpartyClassification>('one_off');
  return (
    <div className="callout" style={{ marginTop: 14 }} data-testid="classify-counterparty">
      <b>{t('classify.question')}</b>
      <p className="muted small" style={{ marginTop: 6 }}>
        {t('classify.why')}
      </p>
      <label style={{ display: 'block', marginTop: 10 }}>
        <input
          type="radio"
          name="counterparty-classification"
          value="one_off"
          checked={choice === 'one_off'}
          onChange={() => setChoice('one_off')}
        />{' '}
        <b>{t('classify.oneOff')}</b> {t('classify.oneOffDetail')}{' '}
        <span className="muted">{t('classify.notSure')}</span>
      </label>
      <label style={{ display: 'block', marginTop: 8 }}>
        <input
          type="radio"
          name="counterparty-classification"
          value="recurring_business"
          checked={choice === 'recurring_business'}
          onChange={() => setChoice('recurring_business')}
        />{' '}
        <b>{t('classify.recurring')}</b> {t('classify.recurringDetail')}
      </label>
      <button
        type="button"
        className="btn"
        style={{ marginTop: 12 }}
        disabled={working}
        onClick={() => onClassify(choice)}
      >
        {t('classify.record')}
      </button>
    </div>
  );
}

